import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Check, ChevronsUpDown, X, Building2, User } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { customersAPI } from '../lib/api';

/**
 * CustomerCombobox — searchable customer picker.
 * Works inside drawers/modals on mobile & desktop.
 *
 * Props:
 *  value       — customer id (string | '')
 *  onChange    — (id: string) => void
 *  className   — optional extra class on trigger
 *  disabled    — boolean
 *  data-testid — forwarded
 */
export function CustomerCombobox({ value, onChange, className, disabled = false, 'data-testid': testId = 'customer-combobox' }) {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const loaded = useRef(false);

  // Fetch full customer list on first open (paginated to handle >100)
  const loadCustomers = useCallback(async () => {
    if (loaded.current) return;
    setLoading(true);
    try {
      let all = [];
      let page = 1;
      const pageSize = 100;
      while (true) {
        const res = await customersAPI.list({ page_size: pageSize, page, is_active: true });
        const batch = res.data.customers || [];
        all = all.concat(batch);
        if (batch.length < pageSize || all.length >= (res.data.total || Infinity)) break;
        page++;
      }
      setCustomers(all);
      loaded.current = true;
    } catch {
      // silent
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) loadCustomers(); }, [open, loadCustomers]);

  // Derive display label for the selected customer
  const selected = customers.find(c => c.id === value);
  const label = selected ? (selected.display_name || [selected.first_name, selected.last_name].filter(Boolean).join(' ') || selected.company_name || 'Unnamed') : '';

  // Client-side filter
  const filtered = search
    ? customers.filter(c => {
        const q = search.toLowerCase();
        return (
          (c.display_name || '').toLowerCase().includes(q) ||
          (c.first_name || '').toLowerCase().includes(q) ||
          (c.last_name || '').toLowerCase().includes(q) ||
          (c.company_name || '').toLowerCase().includes(q) ||
          (c.primary_phone || '').includes(q) ||
          (c.billing_email || '').toLowerCase().includes(q)
        );
      })
    : customers;

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal h-9 px-3', !value && 'text-muted-foreground', className)}
          data-testid={testId}
        >
          <span className="truncate">{value ? label : 'Select customer...'}</span>
          {value ? (
            <X
              className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              data-testid={`${testId}-clear`}
            />
          ) : (
            <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" side="bottom" avoidCollisions>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search customers..."
            value={search}
            onValueChange={setSearch}
            data-testid={`${testId}-search`}
          />
          <CommandList>
            {loading ? (
              <div className="py-4 text-center text-sm text-muted-foreground">Loading...</div>
            ) : (
              <>
                <CommandEmpty>No customers found.</CommandEmpty>
                <CommandGroup>
                  {/* No customer option */}
                  <CommandItem
                    value="_none"
                    onSelect={() => { onChange(''); setOpen(false); setSearch(''); }}
                    data-testid={`${testId}-option-none`}
                  >
                    <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                    <span className="text-muted-foreground">No customer</span>
                  </CommandItem>

                  {filtered.map(c => {
                    const name = c.display_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || 'Unnamed';
                    const secondary = c.company_name && name !== c.company_name ? c.company_name : (c.primary_phone || c.billing_email || '');
                    const isCompany = c.customer_type === 'commercial';

                    return (
                      <CommandItem
                        key={c.id}
                        value={c.id}
                        onSelect={() => { onChange(c.id); setOpen(false); setSearch(''); }}
                        data-testid={`${testId}-option-${c.id}`}
                      >
                        <Check className={cn('mr-2 h-4 w-4 shrink-0', value === c.id ? 'opacity-100' : 'opacity-0')} />
                        <div className="flex items-center gap-2 min-w-0">
                          {isCompany ? <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                          <div className="min-w-0">
                            <p className="text-sm truncate">{name}</p>
                            {secondary && <p className="text-xs text-muted-foreground truncate">{secondary}</p>}
                          </div>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
